/* Why this hook exists at all, rather than `useFinSobCategoryBreakdown`.

   That hook reads `selectCockpitPeriods` — the reader's own period control — and everything below
   it follows: which quarters are summed, and whether the figures come back as actuals or as an
   expected exit. That is the right source for the Revenue & SOB tile, which is there to answer
   `show me the period I picked`. It is the wrong source for this status.

   `QX plan is not achieved` is raised by the server for the quarters that have ENDED, and the
   sheet states its condition as `QX is ended and Actuals < Plan`. Neither half of that mentions
   the reader. On the default `Full year` the period control always holds an open quarter, so the
   breakdown hands back an expected exit and no actuals at all, and the card had nothing to draw
   for a status the server had already raised — the reader's filter silently deciding whether the
   server's finding is shown. So the quarters are taken from the fiscal calendar the cockpit
   already knows (`useEffectiveActualsSync`, scoped to the viewed fiscal year) and each ended
   quarter is aggregated on its own terms; the period control scopes the tiles around this card,
   not the card. */
import type { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  DEFAULT_CURRENCY,
  getFilteredFinSobRows,
  getFinancialAchievementScore,
  getRevenueAndSobByCategory,
  OVERALL_CATEGORY,
} from '../../../../lib'
import { isActualsFinSobRow } from '../../../../lib/revenueAndSob/isActualsFinSobRow'
import { useEffectiveActualsSync } from '../../../../model/hooks'
import {
  selectCockpitCategory,
  selectCockpitCurrency,
  selectCockpitMetric,
  selectVisibleCockpitFinSobRows,
} from '../../../../model/selectors'
import type { CockpitPeriodKey } from '../../../../model/types'

/** The four quarters of a fiscal year, in the period keys the cockpit names them by; the index is
 *  the fiscal quarter number, so `q1` sits at index `0`. */
const QUARTER_PERIODS: CockpitPeriodKey[] = ['q1', 'q2', 'q3', 'q4']

/** One category of one ended quarter, with the plan it had and the actuals it booked. */
export interface IQuarterCategoryFigures {
  /** The quarter the two figures are measured over — never a roll-up across quarters, so a row
   *  can always say which quarter it belongs to. */
  quarter: CockpitPeriodKey
  category: string
  plan: number | null
  actuals: number | null
}

/** A category that finished an ended quarter under its plan — one row of the card's list. */
export interface IQuarterPlanShortfall {
  quarter: CockpitPeriodKey
  category: string
  plan: number
  actuals: number
  /** `actuals / plan` as the cockpit scores it — the same number the Achievement Score table
   *  reads, so a category cannot read short here and on plan there. */
  score: number | null
}

export interface IEndedQuarterShortfalls {
  fYear: number
  currency: string
  /** The quarters of the viewed fiscal year that have closed, ascending. Empty before the first
   *  quarter of the year has ended. */
  endedQuarters: CockpitPeriodKey[]
  /** Every category/quarter pair that carries a plan or an actual — what the count is stated out
   *  of, and what separates `nothing came in short` from `nothing was measured`. */
  measured: IQuarterCategoryFigures[]
  /** Those that came in under plan, worst share first. */
  shortfalls: IQuarterPlanShortfall[]
}

/**
 * The ended quarters of the viewed fiscal year, aggregated per category for one offering, and the
 * categories among them whose actuals came in under plan.
 *
 * Each quarter is aggregated alone — a quarter that was missed and a quarter that was beaten
 * cancel each other out in a sum, and the status is about the quarter that was missed. `isYtd` is
 * false and `isPeriodOpen` is false for the same reason: an ended quarter is closed, so it is
 * scored in full against real actuals rather than prorated or read as an expected exit.
 */
export const useEndedQuarterShortfalls = (
  offering: ProgramTypes | null
): IEndedQuarterShortfalls => {
  const rows = useSelector(selectVisibleCockpitFinSobRows)
  const actualsType = useSelector(selectCockpitMetric)
  const currency = useSelector(selectCockpitCurrency)
  const selectedCategory = useSelector(selectCockpitCategory)
  const { fYear, currentFQuarter, fQuarterActualsPercentage } = useEffectiveActualsSync()

  /* A quarter has ended once the year has moved past it. `currentFQuarter` is scoped to the year
     the cockpit is viewing, so a past year arrives as `5` (all four closed) and a future one as
     `0` (none), and neither needs a case of its own here. */
  const endedQuarters = useMemo(
    () => QUARTER_PERIODS.filter((_, index) => index + 1 < currentFQuarter),
    [currentFQuarter]
  )

  const measured = useMemo<IQuarterCategoryFigures[]>(() => {
    if (offering === null) {
      return []
    }

    return endedQuarters.flatMap((quarter) => {
      const scopedRows = getFilteredFinSobRows({
        rows,
        offering,
        periods: [quarter],
        currentFQuarter,
        category: selectedCategory,
      })

      const categoryRows = getRevenueAndSobByCategory({
        targetRows: scopedRows.filter((row) => row.isTarget),
        actualsRows: scopedRows.filter(isActualsFinSobRow),
        quotaRows: scopedRows.filter((row) => row.isQuota),
        selectedFYear: fYear,
        actualsType,
        isLocalCurrency: currency !== DEFAULT_CURRENCY,
        isYtd: false,
        currentFQuarter,
        fQuarterActualsPercentage,
        isPeriodOpen: false,
        selectedCategory: selectedCategory ?? OVERALL_CATEGORY,
      })

      /* The aggregation's last row is the offering's total, and `isTotal` is how the feature drops
         it. With the cockpit narrowed to one category that flag stops separating the two: the
         total is that category's own figures, and the category's row carries the flag as well.
         Dropping both would answer a narrowed cockpit with `no figures` while the figures stand in
         the Revenue & SOB tile beside it, so the narrowed case keeps one copy and drops the
         repeat. */
      const listedRows =
        selectedCategory !== null
          ? categoryRows.filter(({ category }) => category === selectedCategory).slice(0, 1)
          : categoryRows.filter(({ isTotal }) => !isTotal)

      /* A category the aggregation names but carries neither a plan nor an actual for in this
         quarter is not a category anything is known about: it is no part of the count, and it is
         not a plan that was reached. */
      return listedRows
        .filter(({ sales }) => sales.plan !== null || sales.actuals !== null)
        .map(({ category, sales }) => ({
          quarter,
          category,
          plan: sales.plan,
          actuals: sales.actuals,
        }))
    })
  }, [
    offering,
    endedQuarters,
    rows,
    currentFQuarter,
    selectedCategory,
    fYear,
    actualsType,
    currency,
    fQuarterActualsPercentage,
  ])

  /* A category is short when it has a plan to miss and an actual that missed it. A plan of zero or
     less is not a target the reader can be held to, so it is left out rather than scored against. */
  const shortfalls = useMemo<IQuarterPlanShortfall[]>(
    () =>
      measured
        .flatMap(({ quarter, category, plan, actuals }) =>
          plan === null || plan <= 0 || actuals === null || actuals >= plan
            ? []
            : [
                {
                  quarter,
                  category,
                  plan,
                  actuals,
                  score: getFinancialAchievementScore(actuals, plan),
                },
              ]
        )
        /* Worst first, and the money decides the ties: two categories at the same share are not
           equally worth the reader's next hour. The quarter is deliberately not part of the
           order — a Q1 miss the reader can still answer for outranks a shallow Q3 one. */
        .sort(
          (left, right) =>
            (left.score ?? 0) - (right.score ?? 0) ||
            right.plan - right.actuals - (left.plan - left.actuals)
        ),
    [measured]
  )

  return { fYear, currency, endedQuarters, measured, shortfalls }
}
